import type { AuthError } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { reportError } from './observability';
import { isPlaceholderEmail } from './golferEmail';

/**
 * Single entry point for promoting an email address to an auth identity with
 * a 6-digit code. Mirror of Core's `src/lib/auth/requestEmailOtp.ts` — kept
 * per-app the same way `bookFitting.ts` and `observability.ts` are, because
 * each app has its own Supabase client and placeholder-email helper. Change
 * one, change both.
 *
 * It exists because the send / resend / verify dance had drifted across four
 * surfaces and only some of them got each fix.
 *
 * ── What GoTrue actually does ──────────────────────────────────────────────
 *
 * `updateUser({ email })` starts an email_change. When the project has
 * **Secure email change** on (`[auth.email] double_confirm_changes = true`,
 * Dashboard → Authentication → Emails) and the auth user already has an
 * email, `sendEmailChange` mints TWO codes and mails BOTH addresses:
 *
 *   new address     → otpNew,     hashed as GenerateTokenHash(new_email, otpNew)
 *   current address → otpCurrent, hashed as GenerateTokenHash(email,     otpCurrent)
 *
 * `/verify` hashes the submitted code with the email the CLIENT passes, so a
 * code minted for the current address is only accepted when it is submitted
 * together with the current address. Submitting it with the new address
 * hashes to nothing on file and comes back "Token has expired or is invalid"
 * — that is exactly the reported "two emails arrive, one of them reads as
 * expired". `verifyEmailChangeOtp` retries against the current address so
 * either code works whichever inbox the golfer read it from.
 *
 * Confirming ONE side does not complete the change. GoTrue sets
 * `email_change_confirm_status = 1` and answers 200 with
 * `{"msg":"Confirmation link accepted. Please proceed to confirm link sent to
 * the other email"}` — no session, no user — and supabase-js maps that to
 * `{ data: { user: null, session: null }, error: null }`. Code that only
 * checks `error` therefore reports success on a change that never landed.
 * We detect it (no session + `new_email` still set) and return
 * `second-code-required` so the surface can ask for the other code, which
 * DOES complete the change.
 *
 * ── Why a resend must not be a second updateUser ──────────────────────────
 *
 * `updateUser` re-initiates the whole change. Auth emails are capped
 * project-wide per hour (`[auth.rate_limit] email_sent`), so retrying
 * through it burns the quota for every user on the project, and each retry
 * re-validates the address and rewrites `email_change` from scratch. A
 * resend goes through `auth.resend({ type: 'email_change' })`, which
 * re-sends the change already on the row.
 *
 * ── The resend trap ───────────────────────────────────────────────────────
 *
 * GoTrue's `/resend` resolves the user with
 * `FindUserByEmailAndAudience(params.Email)` — `auth.users.email`, the
 * CURRENT address. Called with the address being changed TO, it finds no
 * row, and the not-found branch answers **200 with `{}`** without sending
 * anything. That is why "Resend code" reported success and no code ever
 * arrived. So the resend is always addressed to the account's current
 * email, with the target only used to check that it is the change actually
 * outstanding. A user with no current email at all (phone-only signup) has
 * nothing to address the resend to, so those fall back to `updateUser` —
 * and they are also the users secure email change skips entirely, since it
 * only engages when `auth.users.email` is non-empty.
 *
 * ── The already-on-the-account trap ───────────────────────────────────────
 *
 * `PUT /user` only starts a change when `params.Email != user.GetEmail()`.
 * Ask it to change to the address the account ALREADY has and it answers 200
 * with the user, mails nothing, and reports no error. Every surface read
 * that as "code sent" and showed a code box for a code that was never going
 * to arrive — the reported "clicking verify again didn't send the OTP".
 * Reached whenever a screen re-asks for an address that is already
 * confirmed, which is why `already-confirmed` is a first-class result here
 * instead of something each caller has to notice on its own.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** GoTrue's frequency limit: "…you can only request this after 42 seconds." */
const RETRY_AFTER_RE = /after (\d+) seconds?/i;

export type EmailOtpSendResult =
  | { ok: true; mode: 'initiated' | 'resent' }
  /**
   * The address is already this account's CONFIRMED sign-in email, so there
   * is no change to confirm and no code to send. Callers must treat this as
   * "already verified" and move on — showing a code box here is the dead end
   * described in the docblock.
   */
  | { ok: true; mode: 'already-confirmed'; email: string }
  | { ok: false; message: string; retryAfterSeconds?: number };

export type EmailOtpVerifyResult =
  /** The address is now on the auth user. */
  | { status: 'confirmed'; email: string }
  /**
   * One of the two secure-email-change codes was accepted; the change only
   * lands once the other one is entered too. `otherEmail` is the inbox
   * holding it, or null when that inbox is not a real address we can reach.
   */
  | { status: 'second-code-required'; message: string; otherEmail: string | null }
  | { status: 'error'; message: string };

export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/** Placeholder / empty addresses can never receive a code. */
function isUnreachableEmail(email: string): boolean {
  return !email || isPlaceholderEmail(email);
}

type ErrorLike = Pick<AuthError, 'message'> & { status?: number; code?: string };

function isRateLimited(err: ErrorLike): boolean {
  return (
    err.status === 429 ||
    (err.code ?? '').includes('rate_limit') ||
    /rate limit|too many/i.test(err.message ?? '')
  );
}

function looksLikeBadToken(err: ErrorLike): boolean {
  return (err.code ?? '') === 'otp_expired' || /expired|invalid/i.test(err.message ?? '');
}

/**
 * Turn a GoTrue send failure into something a golfer can act on. A 429 is
 * the one we must never leak raw — it arrives either as the frequency limit
 * (with a window we can quote) or as the project's hourly cap.
 */
function describeSendError(err: ErrorLike): { message: string; retryAfterSeconds?: number } {
  const raw = (err.message ?? '').trim();
  const seconds = Number(RETRY_AFTER_RE.exec(raw)?.[1] ?? NaN);
  if (Number.isFinite(seconds)) {
    return seconds > 0
      ? {
          message: `Please wait ${seconds} second${seconds === 1 ? '' : 's'} before requesting another code.`,
          retryAfterSeconds: seconds,
        }
      : {
          message: 'Please wait a few seconds before requesting another code.',
          retryAfterSeconds: 1,
        };
  }
  if (isRateLimited(err)) {
    return {
      message:
        'Too many verification emails have been sent in the last hour. Please wait a few minutes and try again.',
    };
  }
  if ((err.code ?? '') === 'email_exists' || /already been registered|already registered/i.test(raw)) {
    return { message: 'That email is already linked to another GolfAuth account.' };
  }
  return { message: raw || 'Could not send a code to that email. Try again.' };
}

function describeVerifyError(err: ErrorLike): string {
  if (isRateLimited(err)) return describeSendError(err).message;
  if (looksLikeBadToken(err)) {
    return 'That code has expired or does not match. Request a new code and try again.';
  }
  return (err.message ?? '').trim() || 'Verification failed. Check the code and try again.';
}

type AuthEmails = {
  accountEmail: string | null;
  /** `email_confirmed_at` is set, i.e. the account email is a proven identity. */
  accountEmailConfirmed: boolean;
  pendingEmail: string | null;
};

/** The account's sign-in address and any outstanding email change. */
async function readAuthEmails(): Promise<AuthEmails> {
  const { data, error } = await supabase.auth.getUser();
  if (error) return { accountEmail: null, accountEmailConfirmed: false, pendingEmail: null };
  return {
    accountEmail: normalizeEmail(data.user?.email) || null,
    accountEmailConfirmed: !!data.user?.email_confirmed_at,
    pendingEmail: normalizeEmail(data.user?.new_email) || null,
  };
}

/**
 * The address of an outstanding email change (`auth.users.email_change`,
 * exposed as `new_email`), normalised, or null when there isn't one.
 */
export async function getOutstandingEmailChange(): Promise<string | null> {
  return (await readAuthEmails()).pendingEmail;
}

/**
 * Dispatch an email_change code. `{ resend: true }` means "refresh the
 * change outstanding for this address" — right for a "Resend code" tap and
 * for re-entering a code-entry screen — and falls back to `updateUser` when
 * there is nothing outstanding for this address (expired, or a different
 * address is pending). Leave it off only when the caller knows this is a
 * fresh change.
 *
 * Never sends `emailRedirectTo` — that is what makes GoTrue email the
 * numeric `{{ .Token }}` rather than only a magic link.
 */
export async function requestEmailOtp(
  email: string,
  { resend = false }: { resend?: boolean } = {},
): Promise<EmailOtpSendResult> {
  const target = normalizeEmail(email);
  if (!EMAIL_RE.test(target)) {
    return { ok: false, message: 'Enter a valid email address.' };
  }
  if (isUnreachableEmail(target)) {
    return { ok: false, message: 'Enter a real email address.' };
  }

  const { accountEmail, accountEmailConfirmed, pendingEmail } = await readAuthEmails();

  // The address is already ON the account, so `updateUser` would be the
  // silent no-op described in the docblock. Answer with what is true.
  if (accountEmail && accountEmail === target) {
    if (accountEmailConfirmed) {
      return { ok: true, mode: 'already-confirmed', email: target };
    }
    // On the account but never confirmed (an admin-set address, or a signup
    // whose confirmation lapsed). The code that confirms THAT is the signup
    // confirmation — there is no email_change to resend.
    const { error } = await supabase.auth.resend({ type: 'signup', email: target });
    if (!error) return { ok: true, mode: 'resent' };
    reportError(error, { scope: 'auth.requestEmailOtp.confirmCurrent' });
    return { ok: false, ...describeSendError(error) };
  }

  if (resend) {
    // Only refresh a change that is actually outstanding for this address,
    // and address the resend to the account email — /resend looks the user
    // up by that column, so anything else is a silent 200 no-op.
    if (pendingEmail === target && accountEmail) {
      const { error } = await supabase.auth.resend({
        type: 'email_change',
        email: accountEmail,
      });
      if (!error) return { ok: true, mode: 'resent' };
      // A 429 must surface as-is: re-initiating with updateUser would hit
      // the same cap and burn the hourly quota the golfer is waiting on.
      if (isRateLimited(error)) {
        reportError(error, { scope: 'auth.requestEmailOtp.resend', extra: { rateLimited: true } });
        return { ok: false, ...describeSendError(error) };
      }
      reportError(error, { scope: 'auth.requestEmailOtp.resend' });
      // Otherwise the outstanding change is gone server-side (expired
      // tokens); re-initiating is the only way to get the golfer a code.
    }
  }

  const { error } = await supabase.auth.updateUser({ email: target });
  if (!error) return { ok: true, mode: 'initiated' };
  reportError(error, {
    scope: 'auth.requestEmailOtp.initiate',
    extra: { rateLimited: isRateLimited(error), resendRequested: resend },
  });
  return { ok: false, ...describeSendError(error) };
}

/**
 * Confirm an email_change code against the address the token was minted
 * for, then work out whether the change actually landed. See the module
 * docblock — under secure email change a single code is accepted but does
 * not complete the change, and supabase-js reports that as success.
 */
export async function verifyEmailChangeOtp(
  email: string,
  token: string,
): Promise<EmailOtpVerifyResult> {
  const target = normalizeEmail(email);
  const code = token.trim();
  if (!/^\d{6}$/.test(code)) {
    return { status: 'error', message: 'Enter the 6-digit code.' };
  }

  const { data: before } = await supabase.auth.getUser();
  const currentEmail = normalizeEmail(before?.user?.email) || null;
  const currentConfirmed = !!before?.user?.email_confirmed_at;
  const pendingBefore = normalizeEmail(before?.user?.new_email);

  if (currentEmail === target && !pendingBefore) {
    // Already landed. A re-opened modal or a double tap must report the state
    // the server is actually in, not spend the code on a /verify that can only
    // answer "expired" — that dead end is what made a successful verification
    // look like a failure. Reporting it grants nothing: the address is already
    // a confirmed identity on this auth user.
    if (currentConfirmed) return { status: 'confirmed', email: target };
    // On the account but unconfirmed, so the code `requestEmailOtp` dispatched
    // was a SIGNUP confirmation. /verify hashes both kinds the same way but
    // only accepts the matching type, so asking for email_change here would
    // reject a perfectly good code.
    const { error } = await supabase.auth.verifyOtp({
      email: target,
      token: code,
      type: 'signup',
    });
    if (error) {
      if (!looksLikeBadToken(error)) {
        reportError(error, { scope: 'auth.verifyEmailChangeOtp.confirmCurrent' });
      }
      return { status: 'error', message: describeVerifyError(error) };
    }
    return { status: 'confirmed', email: target };
  }

  let attemptedWith = target;
  let result = await supabase.auth.verifyOtp({ email: target, token: code, type: 'email_change' });

  if (
    result.error &&
    looksLikeBadToken(result.error) &&
    currentEmail &&
    currentEmail !== target
  ) {
    attemptedWith = currentEmail;
    result = await supabase.auth.verifyOtp({
      email: currentEmail,
      token: code,
      type: 'email_change',
    });
  }

  if (result.error) {
    if (!looksLikeBadToken(result.error)) {
      reportError(result.error, { scope: 'auth.verifyEmailChangeOtp' });
    }
    return { status: 'error', message: describeVerifyError(result.error) };
  }

  // Only the FINAL confirmation issues a refresh token.
  if (result.data.session?.access_token) {
    return { status: 'confirmed', email: target };
  }

  const { data: after } = await supabase.auth.getUser();
  const stillPending = normalizeEmail(after?.user?.new_email);
  if (!stillPending) {
    if (normalizeEmail(after?.user?.email) === target) {
      return { status: 'confirmed', email: target };
    }
    reportError(
      new Error('email_change verify returned no session and left no pending change'),
      { scope: 'auth.verifyEmailChangeOtp.indeterminate' },
    );
    return {
      status: 'error',
      message: 'We could not confirm that code. Request a new code and try again.',
    };
  }

  // The code we just consumed belongs to whichever address we verified it
  // against, so the outstanding one is the other side of the pair.
  const otherEmail = attemptedWith === target ? currentEmail : target;
  if (!otherEmail || isUnreachableEmail(otherEmail)) {
    // Nothing the golfer can do: secure email change is waiting on a code we
    // mailed to a placeholder address. Needs the Dashboard setting off.
    reportError(
      new Error('secure email change is waiting on an unreachable confirmation inbox'),
      {
        scope: 'auth.verifyEmailChangeOtp.unreachableSecondInbox',
        extra: { hasCurrentEmail: !!currentEmail },
      },
    );
    return {
      status: 'second-code-required',
      otherEmail: null,
      message:
        'That code was accepted, but this account also has to confirm the change from its previous email address, which we can’t reach. Email support@golfauth.com and we’ll finish it for you.',
    };
  }
  return {
    status: 'second-code-required',
    otherEmail,
    message: `That code was accepted. This account confirms email changes from both addresses, so we also sent a code to ${otherEmail} — enter that code to finish.`,
  };
}
